import React from "react";
import { safeText } from "../../shared/formatters.js";
import { useModalA11y } from "../../shared/hooks.js";
import { useClientContext } from "../../ClientContext.jsx";

interface Notification {
  id?: string;
  type?: string;
  title?: string;
  message?: string;
  read?: boolean;
  created_at?: string;
}

interface NotificationsPanelProps {
  notifOpen: boolean;
  notifications: Notification[];
  onClose: () => void;
  col: boolean;
}

export default function NotificationsPanel({
  notifOpen,
  notifications,
  onClose,
  col,
}: NotificationsPanelProps) {
  const { T } = useClientContext();
  const trapRef = useModalA11y(onClose);
  if (!notifOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{ position: "fixed", inset: 0, zIndex: 199 }}
      />
      {/* Panel */}
      <div
        ref={trapRef as React.LegacyRef<HTMLDivElement>}
        role="region"
        aria-label="Notificações"
        style={{
          position: "fixed",
          bottom: 80,
          left: col ? 80 : 250,
          zIndex: 200,
          width: 320,
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: 14,
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "14px 18px",
            borderBottom: `1px solid ${T.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>🔔 Notificações</span>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: T.sub,
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        {notifications.length === 0 ? (
          <div style={{ padding: "28px 18px", textAlign: "center", color: T.sub, fontSize: 13 }}>
            Nenhuma notificação.
          </div>
        ) : (
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {notifications.map((n, i) => (
              <div
                key={n.id || i}
                style={{
                  padding: "12px 18px",
                  borderBottom: `1px solid ${T.border}`,
                  background: n.read ? "transparent" : `${T.accent}08`,
                }}
              >
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 16 }}>
                    {{
                      novo_snapshot: "📊",
                      performance: "🏆",
                      suitability: "📋",
                      info: "ℹ️",
                    }[n.type || ""] || "🔔"}
                  </span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                      {safeText(n.title, "notif.title")}
                    </div>
                    {n.message && (
                      <div style={{ fontSize: 12, color: T.sub, marginTop: 2 }}>
                        {safeText(n.message, "notif.message")}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
                      {safeText(
                        typeof n.created_at === "string"
                          ? new Date(n.created_at).toLocaleDateString("pt-BR")
                          : "–",
                        "notif.date"
                      )}
                    </div>
                  </div>
                  {!n.read && (
                    <div
                      style={{
                        marginLeft: "auto",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#DC2626",
                        flexShrink: 0,
                        marginTop: 4,
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
